/**
 * THM-004: an account the owner has gifted every theme and cue style (D-054).
 *
 * The server says so in `/api/me`, but that answer arrives after the page has already
 * read the saved settings, and a saved theme that is not offered falls back to the
 * default (D-047). So the device remembers the last answer, and the page starts from it;
 * when the server's answer differs, the page saves the new one and reloads once.
 *
 * A hint, not access control (CLAUDE.md §10): themes are colours on this device and
 * change nothing anyone else sees, a shot or a score. Editing it only recolours the
 * editor's own page. Anything that is ever paid for is checked by the server.
 */
import type { KeyValueStore } from './settings.ts';
import type { AccountState } from '../ui/account-section.ts';

export const UNLOCK_KEY = 'potted.unlocked.v1';

/** The last answer this device was given. Anything but "1" is no. */
export function readUnlockHint(storage: KeyValueStore | undefined): boolean {
  try {
    return storage?.getItem(UNLOCK_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeUnlockHint(storage: KeyValueStore | undefined, unlocked: boolean): void {
  try {
    if (unlocked) storage?.setItem(UNLOCK_KEY, '1');
    else storage?.removeItem?.(UNLOCK_KEY);
  } catch {
    // Blocked storage: the page simply starts from the free set next time.
  }
}

/**
 * The new answer, when the server's differs from what the page started with, else
 * undefined. Only a definite answer counts: offline or unavailable changes nothing.
 */
export function unlockChange(state: AccountState, startedWith: boolean): boolean | undefined {
  if (state.kind !== 'signed-in' && state.kind !== 'signed-out') return undefined;
  const now = state.kind === 'signed-in' && state.unlocked;
  return now === startedWith ? undefined : now;
}
